import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { FaqSection, LinkClusterSection } from "@/app/components/seo-support";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";
import { EN_ROUTE_FAQS } from "@/app/seo-content-en";

const pillars = [
  {
    title: "Protocol",
    subtitle: "Single source of truth",
    body:
      "Contracts define the request shape, token model, state machine, and transport abstraction. Every Hotline inherits the same outer rules from that source.",
    bg: "#A3E635",
    textClass: "text-black",
  },
  {
    title: "Client",
    subtitle: "Local runtime group",
    body:
      "Caller Controller and Responder Controller live in the user's own runtime. A team can run one side or both sides on the same machine for local proof loops.",
    bg: "#3B82F6",
    textClass: "text-white",
  },
  {
    title: "Platform",
    subtitle: "Hosted control plane",
    body:
      "Catalog, token, delivery metadata, and metrics belong to the control plane. Platform is the hosted public form of that surface.",
    bg: "#8B5CF6",
    textClass: "text-white",
  },
  {
    title: "Selfhost",
    subtitle: "Private control plane",
    body:
      "Selfhost is the same control-plane shape deployed on your own infrastructure. The protocol stays the same; the operating boundary changes.",
    bg: "#FACC15",
    textClass: "text-black",
  },
];

const surfaces = [
  {
    title: "Control plane",
    body:
      "HTTPS requests to Platform or Selfhost handle catalog lookup, token issuance, delivery metadata, and metrics. This is where operating policy lives.",
    accent: "#8B5CF6",
  },
  {
    title: "Data plane",
    body:
      "TaskContract and ResultPackage move between Caller and Responder through TransportAdapter. The important boundary is that Platform does not relay the business payload hop-by-hop.",
    accent: "#A3E635",
  },
];

const architectureFaqs = EN_ROUTE_FAQS["/en/docs/protocol-architecture/"] ?? [];

export default function EnglishProtocolArchitecturePage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <ContentHero
        eyebrow="DOCS · PROTOCOL ARCHITECTURE · EN"
        eyebrowColor="#A3E635"
        titleTop="Protocol architecture"
        titleBottom="control plane and data plane"
        bottomColor="#A3E635"
        lead="This page isolates the engineering layer that used to be folded into the homepage. It explains how a Hotline call is initiated, where operating policy lives, and why Platform or Selfhost should not be mistaken for the business data path."
        meta={["Protocol", "Control plane", "Data plane", "Selfhost"]}
        breadcrumbs={[
          { label: "English Home", href: "/en/" },
          { label: "Docs", href: "/en/docs/" },
          { label: "Protocol Architecture" },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {pillars.map((pillar) => (
            <article
              key={pillar.title}
              className="border-[6px] border-black p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]"
              style={{ backgroundColor: pillar.bg }}
            >
              <p className={`text-xs font-black uppercase tracking-[0.22em] ${pillar.textClass}`}>{pillar.subtitle}</p>
              <h2 className={`mt-3 text-2xl font-black uppercase ${pillar.textClass}`}>{pillar.title}</h2>
              <p className={`mt-4 text-sm font-bold leading-7 ${pillar.textClass}`}>{pillar.body}</p>
            </article>
          ))}
        </section>

        <section className="mt-12 grid gap-5 lg:grid-cols-2">
          {surfaces.map((surface) => (
            <article
              key={surface.title}
              className="border-[6px] border-black bg-white/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8"
            >
              <p className="text-xs font-black uppercase tracking-[0.22em]" style={{ color: surface.accent }}>
                OPERATING SURFACE
              </p>
              <h2 className="mt-3 text-2xl font-black uppercase text-black sm:text-3xl">{surface.title}</h2>
              <p className="mt-4 text-sm font-bold leading-7 text-zinc-800 sm:text-base">{surface.body}</p>
            </article>
          ))}
        </section>

        <aside className="mt-12 border-[6px] border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#A3E635]">ONE SENTENCE</p>
          <h2 className="mt-3 text-2xl font-black uppercase sm:text-3xl">What matters most</h2>
          <p className="mt-4 text-sm font-bold leading-7 text-zinc-300 sm:text-base">
            CALL ANYTHING standardizes discovery, identity, settlement, and observability at the protocol layer while keeping the actual capability exchange between Caller and Responder on the data plane.
          </p>
        </aside>

        <FaqSection
          eyebrow="PAGE FAQ"
          title="3 questions engineers usually ask first"
          intro="These are the boundary questions that matter before you decide whether to integrate, self-host, or compare the model against adjacent tool protocols."
          items={architectureFaqs}
          accent="#A3E635"
        />

        <LinkClusterSection
          eyebrow="NEXT"
          title="Where to continue"
          intro="Once the control-plane split is clear, move to one stable evidence page or one runnable proof path."
          accent="#A3E635"
          links={[
            {
              href: "/en/docs/protocol-examples/",
              title: "Protocol Examples",
              summary: "See the minimal request, result_package, error, and registration payload shapes.",
              meta: "Request · result_package · error",
            },
            {
              href: "/en/glossary/",
              title: "Glossary",
              summary: "Lock down the canonical meanings of Hotline, Caller, Responder, and Template Bundle.",
              meta: "Definitions · canonical terms",
            },
            {
              href: "/en/docs/quick-start-caller/",
              title: "Caller Quick Start",
              summary: "Map the architecture back to one real local loop with commands and one result_package.",
              meta: "Quick Start · local loop",
            },
            {
              href: "/en/compare/hotline-vs-mcp/",
              title: "Hotline vs MCP",
              summary: "Use the compare page when the real question is whether this is just another tool invocation surface.",
              meta: "Compare · positioning",
            },
          ]}
        />
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
